import {useCallback, useEffect, useMemo, useState} from 'react';

//Components
import DownsellModal from './DownsellModal/DownsellModal';
import Modal, {useModal} from '@stencil/hotels-modal-component';

// Hooks
import useNavigateToReviewPage from '@stencil/navigate-to-review-page-dweb/src/navigateToReview';

// Utils
import {isObjectEmpty, isValidArray} from '@stencil/hotels-utils/src/common-utils';
import {getSlashAndShowPriceCg} from '@stencil/hotels-utils/src/get-details-slash-and-show-price';

// Omniture
import useOmnitureLoaded from '@stencil/omniture-analytics/src/hooks/useOmnitureLoaded';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';
import {omnitureConfig, omnitureThunk} from '@stencil/omniture-analytics';

// Types
import {LinkedRatePlans} from '@stencil/hotels-types/src/details-cg/types';

// Styles
import * as S from './DownsellUI.styles';

interface DownsellUIProps {
  linkedRatePlan?: LinkedRatePlans;
  activeBankCode?: string;
  roomCode?: string;
}

const DownsellUI = ({linkedRatePlan, activeBankCode, roomCode}: DownsellUIProps) => {
  const {ratePlan: linkedRatePlanData, data: linkedRateSheetData} = linkedRatePlan || {};
  const firstTariff = linkedRatePlanData?.tariffs?.[0];

  const {isModalOpen, closeModal, openModal} = useModal();

  const [navigatorNew] = useNavigateToReviewPage();

  useOmnitureLoaded(detailsClick.roomSelection.nrVisible);

  const linkedInclusionList = useMemo(() => {
    if (!isValidArray(linkedRatePlanData?.inclusionsList)) {
      return null;
    } else {
      return linkedRatePlanData?.inclusionsList;
    }
  }, [linkedRatePlanData?.inclusionsList]);

  const continueToReviewPageHandler = useCallback(() => {
    const rootLevelInfo = {searchType: 'E', promoCode: activeBankCode};
    const selectedRoomData = {
      ratePlanArr: [{...linkedRatePlanData}],
      roomLevelInfo: {roomCode},
    };

    navigatorNew({
      selectedRoomInfo: [
        {
          ...selectedRoomData,
        },
      ],
      rootLevelInfo: {
        ...rootLevelInfo,
      },
    });

    omnitureThunk(omnitureConfig.pages.RoomSelectionPageUnif, omnitureConfig.events.ctaClick, {
      cta: {
        name: `${detailsClick.roomSelection.nrContinueClicked}`,
        type: omnitureConfig.ctaType.Card,
        componentName: DetailsPageComponents.NRCard,
        destination: ``,
      },
    });
  }, [activeBankCode, linkedRatePlanData, roomCode]);

  const handleOpenModal = () => {
    openModal();
    omnitureThunk(omnitureConfig.pages.RoomSelectionPageUnif, omnitureConfig.events.ctaClick, {
      cta: {
        name: `${detailsClick.roomSelection.nrApplyClicked}`,
        type: omnitureConfig.ctaType.Card,
        componentName: DetailsPageComponents.NRCard,
        destination: ``,
      },
    });
  };

  if (isObjectEmpty(linkedRateSheetData)) {
    return null;
  }

  const {showPrice, tnc, linkedRPOriginalPriceMsg, linkedRPDiscountMsg, linkedRPBottomSheetTitle} =
    getSlashAndShowPriceCg(firstTariff, activeBankCode);

  return (
    <>
      <S.NRUpsellCard>
        <S.NRUpsellTextSection>
          <S.NRUpsellTopWrapper>
            <S.NRUpsellHeading
              dangerouslySetInnerHTML={{
                __html: linkedRateSheetData?.title,
              }}
            />
            <S.NRUpsellPrice
              data-testid="downsell-discount"
              dangerouslySetInnerHTML={{__html: linkedRPDiscountMsg}}
            />
          </S.NRUpsellTopWrapper>
          <S.NRUpsellSubHeading>{linkedRateSheetData?.description}</S.NRUpsellSubHeading>
        </S.NRUpsellTextSection>
        <S.NRUpsellRightSection>
          <S.NRUpsellButton role="button" onClick={handleOpenModal} disabled={isModalOpen}>
            {linkedRateSheetData?.ctaText}
          </S.NRUpsellButton>
        </S.NRUpsellRightSection>
      </S.NRUpsellCard>
      <Modal
        isOpen={isModalOpen}
        closeModal={closeModal}
        modalStyles={{
          width: '51rem',
        }}
        hideCloseButton
      >
        <DownsellModal
          linkedRPBottomSheetTitle={linkedRPBottomSheetTitle}
          onClose={closeModal}
          linkedRateSheetData={linkedRateSheetData}
          linkedRPOriginalPriceMsg={linkedRPOriginalPriceMsg}
          linkedInclusionList={linkedInclusionList}
          showPrice={showPrice}
          continueToReviewPageHandler={continueToReviewPageHandler}
          tnc={tnc}
        />
      </Modal>
    </>
  );
};

export default DownsellUI;
